(() => {
  const init = (dialog) => {
    if (dialog.dataset.mgLiveSearchReady || typeof dialog.showModal !== 'function') return;
    dialog.dataset.mgLiveSearchReady = 'true';
    const input = dialog.querySelector('[data-mg-search-input]');
    const form = dialog.querySelector('[data-mg-search-form]');
    const results = dialog.querySelector('[data-mg-search-results]');
    const status = dialog.querySelector('[data-mg-search-status]');
    const clear = dialog.querySelector('[data-mg-search-clear]');
    const start = results.querySelector('[data-mg-search-start]').cloneNode(true);
    const triggers = [...document.querySelectorAll('[data-mg-live-search-target]')]
      .filter((node) => node.dataset.mgLiveSearchTarget === dialog.id);
    let timer;
    let controller;
    let version = 0;
    let returnFocus;
    let ignoreTriggerFocus = false;

    const query = () => input.value.trim().slice(0, 200);
    const viewport = () => dialog.style.setProperty('--mg-search-viewport-height', `${window.visualViewport?.height || window.innerHeight}px`);
    const cancel = () => {
      window.clearTimeout(timer);
      controller?.abort();
      version += 1;
      results.removeAttribute('aria-busy');
    };
    const showStart = () => {
      results.replaceChildren(start.cloneNode(true));
      results.scrollTop = 0;
      status.textContent = 'Scrivi e i risultati appariranno qui.';
      clear.hidden = !input.value;
    };
    const close = () => {
      if (!dialog.open) return;
      ignoreTriggerFocus = true;
      dialog.close();
    };
    const errorState = () => {
      const message = document.createElement('div');
      message.className = 'mg-live-search__empty';
      const heading = document.createElement('h3');
      heading.textContent = 'Non riesco a caricare i risultati.';
      const detail = document.createElement('p');
      detail.textContent = 'La tua ricerca è qui. Riprova tra un momento.';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'mg-live-search__retry';
      retry.dataset.mgSearchRetry = '';
      retry.textContent = 'Riprova';
      message.append(heading, detail, retry);
      results.replaceChildren(message);
      status.textContent = 'Ricerca temporaneamente non disponibile.';
    };

    const load = async (mode = 'suggestions', page = 1, append = false) => {
      cancel();
      const term = query();
      clear.hidden = !input.value;
      if (!term) { showStart(); return; }
      const ticket = version;
      controller = new AbortController();
      const requestController = controller;
      const timeout = window.setTimeout(() => requestController.abort(), 15000);
      const action = results.querySelector('[data-mg-search-all], [data-mg-search-next]');
      if (action) action.disabled = true;
      results.setAttribute('aria-busy', 'true');
      status.textContent = append ? 'Caricamento di altri prodotti…' : 'Ricerca in corso…';
      const url = new URL(mode === 'suggestions' ? dialog.dataset.suggestUrl : dialog.dataset.searchUrl, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('section_id', 'mg-live-search-results');
      if (mode === 'suggestions') {
        url.searchParams.set('resources[type]', 'product,collection,page');
        url.searchParams.set('resources[limit]', '8');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
      } else {
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        url.searchParams.set('page', String(page));
      }
      try {
        const response = await fetch(url.href, { signal: requestController.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Search returned ${response.status}`);
        const html = await response.text();
        if (ticket !== version || term !== query() || !dialog.open) return;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const content = documentFragment.querySelector('[data-mg-search-response]');
        if (!content) throw new Error('Search response is unavailable');
        const count = Number(content.dataset.mgResultCount) || 0;
        if (append) {
          const grid = results.querySelector('[data-mg-search-grid]');
          const incoming = content.querySelector('[data-mg-search-grid]');
          if (!grid || !incoming) throw new Error('Missing search results');
          const known = new Set([...grid.querySelectorAll('a')].map((link) => new URL(link.href).pathname));
          [...incoming.children].forEach((node) => {
            const path = new URL(node.getAttribute('href'), window.location.origin).pathname;
            if (!known.has(path)) { grid.append(node); known.add(path); }
          });
          results.querySelector('[data-mg-search-next]')?.remove();
          const next = content.querySelector('[data-mg-search-next]');
          if (next) results.querySelector('[data-mg-search-response]').append(next);
        } else {
          results.replaceChildren(content);
          results.scrollTop = 0;
        }
        const shown = results.querySelectorAll('[data-mg-search-product]').length;
        status.textContent = !count ? `Nessun risultato per “${term}”.` : mode === 'full' ? `${shown} di ${count} prodotti.` : `Risultati per “${term}”.`;
      } catch (error) {
        if (ticket !== version || !dialog.open) return;
        if (append) {
          if (action?.isConnected) action.disabled = false;
          status.textContent = 'Non riesco a caricare altri prodotti. Premi di nuovo per riprovare.';
        } else {
          errorState();
        }
      } finally {
        window.clearTimeout(timeout);
        if (ticket === version) {
          results.removeAttribute('aria-busy');
          if (action?.isConnected) action.disabled = false;
        }
      }
    };

    const schedule = () => {
      cancel();
      clear.hidden = !input.value;
      if (!query()) { showStart(); return; }
      results.setAttribute('aria-busy', 'true');
      status.textContent = 'Ricerca in corso…';
      timer = window.setTimeout(() => load(), 220);
    };
    const open = (trigger, value = '') => {
      if (ignoreTriggerFocus || dialog.open) return;
      returnFocus = trigger;
      input.value = value;
      viewport();
      // Close the existing header menus before making the background inert.
      document.querySelector('[data-mg-header] .mg-header[data-menu-open="true"] [data-mg-menu-toggle]')?.click();
      dialog.showModal();
      document.documentElement.classList.add('mg-live-search-open');
      triggers.forEach((node) => node.querySelector('button')?.setAttribute('aria-expanded', 'true'));
      input.focus({ preventScroll: true });
      if (query()) load(); else showStart();
    };
    const setHeaderInputFocusability = () => triggers.forEach((node) => {
      const headerInput = node.querySelector('input[name="q"]');
      if (headerInput) headerInput.tabIndex = getComputedStyle(headerInput).opacity === '0' ? -1 : 0;
    });
    triggers.forEach((node) => {
      const headerInput = node.querySelector('input[name="q"]');
      const button = node.querySelector('button');
      node.addEventListener('submit', (event) => { event.preventDefault(); open(button, headerInput?.value || ''); });
      headerInput?.addEventListener('focus', () => open(headerInput, headerInput.value));
    });
    form.addEventListener('submit', (event) => { event.preventDefault(); load('full'); });
    input.addEventListener('input', (event) => { if (!event.isComposing) schedule(); });
    input.addEventListener('compositionend', schedule);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.key === 'ArrowDown') {
        const first = results.querySelector('a');
        if (first) { event.preventDefault(); first.focus(); }
      }
    });
    clear.addEventListener('click', () => { cancel(); input.value = ''; showStart(); input.focus({ preventScroll: true }); });
    dialog.querySelector('[data-mg-search-close]').addEventListener('click', close);
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
      }
    });
    results.addEventListener('click', (event) => {
      const term = event.target.closest('[data-mg-search-term]');
      if (term) { input.value = term.dataset.mgSearchTerm; input.focus({ preventScroll: true }); load(); return; }
      if (event.target.closest('[data-mg-search-all]')) { load('full'); return; }
      const next = event.target.closest('[data-mg-search-next]');
      if (next) { load('full', Number(next.dataset.mgSearchNext), true); return; }
      if (event.target.closest('[data-mg-search-retry]')) { load(); return; }
      if (event.target.closest('a[href]')) close();
    });
    dialog.addEventListener('close', () => {
      cancel();
      ignoreTriggerFocus = true;
      document.documentElement.classList.remove('mg-live-search-open');
      triggers.forEach((node) => {
        node.querySelector('button')?.setAttribute('aria-expanded', 'false');
        const headerInput = node.querySelector('input[name="q"]');
        if (headerInput) headerInput.value = input.value;
      });
      returnFocus?.focus({ preventScroll: true });
      requestAnimationFrame(() => { ignoreTriggerFocus = false; });
    });
    window.addEventListener('resize', () => { setHeaderInputFocusability(); if (dialog.open) viewport(); });
    window.visualViewport?.addEventListener('resize', () => { if (dialog.open) viewport(); });
    setHeaderInputFocusability();
  };
  document.querySelectorAll('[data-mg-live-search]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-mg-live-search]').forEach(init));
})();
